import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import { InputTextarea } from 'primereact/inputtextarea'
import { Message } from 'primereact/message'
import { Divider } from 'primereact/divider'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { Gender, MembershipStatus, PaymentStatus } from 'shared'
import axios from 'axios'

const createMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  dateOfBirth: z.date().optional(),
  gender: z.nativeEnum(Gender).optional(),
  membershipStatus: z.nativeEnum(MembershipStatus),
  joinDate: z.date(),
  membershipFee: z.number().min(0).optional(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  earnsIncome: z.boolean(),
  notes: z.string().optional(),
  address: z.object({
    street1: z.string().min(1, 'Street address is required'),
    street2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    zipCode: z.string().min(1, 'ZIP code is required'),
    country: z.string().min(1, 'Country is required'),
  }).optional(),
})

type CreateMemberFormData = z.infer<typeof createMemberSchema>

interface AddMemberModalProps {
  visible: boolean
  onHide: () => void
  onSuccess: () => void
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({ visible, onHide, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string>('')
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [formData, setFormData] = useState<CreateMemberFormData | null>(null)



  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateMemberFormData>({
    resolver: zodResolver(createMemberSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      membershipStatus: MembershipStatus.PENDING,
      joinDate: new Date(),
      membershipFee: 12,
      paymentStatus: PaymentStatus.PENDING,
      earnsIncome: false,
      notes: '',
      address: {
        street1: '',
        street2: '',
        city: '',
        zipCode: '',
        country: 'Germany',
      },
    },
  })

  const genderOptions = [
    { label: 'Male', value: Gender.MALE },
    { label: 'Female', value: Gender.FEMALE },
    { label: 'Other', value: Gender.OTHER },
    { label: 'Prefer not to say', value: Gender.PREFER_NOT_TO_SAY },
  ]

  const statusOptions = [
    { label: 'Active', value: MembershipStatus.ACTIVE },
    { label: 'Pending', value: MembershipStatus.PENDING },
    { label: 'Inactive', value: MembershipStatus.INACTIVE },
    { label: 'Suspended', value: MembershipStatus.SUSPENDED },
    { label: 'Terminated', value: MembershipStatus.TERMINATED },
  ]

  const paymentStatusOptions = [
    { label: 'Paid', value: PaymentStatus.PAID },
    { label: 'Pending', value: PaymentStatus.PENDING },
    { label: 'Overdue', value: PaymentStatus.OVERDUE },
    { label: 'Exempt', value: PaymentStatus.EXEMPT },
  ]


  const onSubmit = (data: CreateMemberFormData) => {
    setFormData(data)
    setShowConfirmation(true)
  }

  const handleConfirmedSubmit = async () => {
    if (!formData) return

    try {
      setIsSubmitting(true)
      setError('')

      // Add default member role since we removed the dropdown
      // Also ensure membershipFee has a valid default if undefined
      const dataWithRole = {
        ...formData,
        memberRoles: ['MEMBER'],
        membershipFee: formData.membershipFee ?? 12
      }

      const response = await axios.post(`${API_BASE_URL}/api/members`, dataWithRole, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.data.success) {
        reset()
        setShowConfirmation(false)
        onSuccess()
        onHide()
      } else {
        setError(response.data.error?.message || 'Failed to create member')
      }
    } catch (err: unknown) {
      console.error('❌ Failed to create member:', err)

      let errorMessage = 'Failed to create member'
      let errorDetails = ''

      if (err instanceof Error && 'response' in err) {
        const axiosError = err as { response?: { data?: { error?: { message?: string, code?: string, details?: any } } } }
        const apiError = axiosError.response?.data?.error

        console.error('📤 API Error Response:', axiosError.response?.data)

        if (apiError?.message) {
          errorMessage = apiError.message
        }

        if (apiError?.code) {
          errorDetails = ` (Error: ${apiError.code})`
          console.error('🔍 Error Code:', apiError.code)
        }

        if (apiError?.details) {
          console.error('🔍 Error Details:', apiError.details)
        }
      }

      setError(errorMessage + errorDetails)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDialogHide = () => {
    if (!isSubmitting) {
      reset()
      setError('')
      setShowConfirmation(false)
      setFormData(null)
      onHide()
    }
  }

  const renderConfirmationContent = () => {
    if (!formData) return null

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Please confirm the member details:</h3>

        <div className="grid grid-cols-2 gap-4">
          <div><strong>Name:</strong> {formData.firstName} {formData.lastName}</div>
          <div><strong>Email:</strong> {formData.email}</div>
          <div><strong>Phone:</strong> {formData.phone || 'N/A'}</div>
          <div><strong>Status:</strong> {formData.membershipStatus}</div>
          <div><strong>Join Date:</strong> {formData.joinDate.toLocaleDateString()}</div>
          <div><strong>Payment Status:</strong> {formData.paymentStatus}</div>
          <div><strong>Earns Income:</strong> {formData.earnsIncome ? 'Yes' : 'No'}</div>
        </div>

        {formData.address && (
          <div>
            <h4 className="font-semibold">Address:</h4>
            <p>{formData.address.street1}</p>
            {formData.address.street2 && <p>{formData.address.street2}</p>}
            <p>{formData.address.zipCode} {formData.address.city}</p>
            <p>{formData.address.country}</p>
          </div>
        )}


        {formData.notes && (
          <div>
            <h4 className="font-semibold">Notes:</h4>
            <p>{formData.notes}</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <Dialog
        visible={visible}
        onHide={handleDialogHide}
        header="Add New Member"
        modal
        className="w-11/12 max-w-4xl"
        draggable={false}
        resizable={false}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <Message severity="error" text={error} className="w-full" />
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="firstName"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.firstName ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.firstName && (
                  <small className="text-red-500 mt-1 block">{errors.firstName.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="lastName"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.lastName ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.lastName && (
                  <small className="text-red-500 mt-1 block">{errors.lastName.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="email"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.email ? 'p-invalid' : ''}`}
                      type="email"
                    />
                  )}
                />
                {errors.email && (
                  <small className="text-red-500 mt-1 block">{errors.email.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="phone"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className="w-full"
                      type="tel"
                    />
                  )}
                />
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="gender"
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={genderOptions}
                      placeholder="Select gender"
                      className="w-full"
                      showClear
                    />
                  )}
                />
              </div>

              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="dateOfBirth"
                      value={field.value || null}
                      onChange={field.onChange}
                      className="w-full"
                      dateFormat="dd.mm.yy"
                      showIcon
                      maxDate={new Date()}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <Divider />

          {/* Membership Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Membership Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="membershipStatus" className="block text-sm font-medium text-gray-700 mb-2">
                  Membership Status *
                </label>
                <Controller
                  name="membershipStatus"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="membershipStatus"
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={statusOptions}
                      className={`w-full ${errors.membershipStatus ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.membershipStatus && (
                  <small className="text-red-500 mt-1 block">{errors.membershipStatus.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="joinDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Join Date *
                </label>
                <Controller
                  name="joinDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="joinDate"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.joinDate ? 'p-invalid' : ''}`}
                      dateFormat="dd.mm.yy"
                      showIcon
                      maxDate={new Date()}
                    />
                  )}
                />
                {errors.joinDate && (
                  <small className="text-red-500 mt-1 block">{errors.joinDate.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Status *
                </label>
                <Controller
                  name="paymentStatus"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="paymentStatus"
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={paymentStatusOptions}
                      className={`w-full ${errors.paymentStatus ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.paymentStatus && (
                  <small className="text-red-500 mt-1 block">{errors.paymentStatus.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="membershipFee" className="block text-sm font-medium text-gray-700 mb-2">
                  Membership Fee (€)
                </label>
                <Controller
                  name="membershipFee"
                  control={control}
                  render={({ field: { onChange, value, ref, ...rest } }) => {
                    const [localValue, setLocalValue] = useState(value?.toString() ?? '12')

                    useEffect(() => {
                      setLocalValue(value?.toString() ?? '12')
                    }, [value])

                    return (
                      <InputText
                        id="membershipFee"
                        {...rest}
                        ref={ref}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={() => {
                          const num = parseFloat(localValue)
                          onChange(localValue === '' ? undefined : (isNaN(num) ? undefined : num))
                        }}
                        className={`w-full ${errors.membershipFee ? 'p-invalid' : ''}`}
                        keyfilter="num"
                      />
                    )
                  }}
                />
                {errors.membershipFee && (
                  <small className="text-red-500 mt-1 block">{errors.membershipFee.message}</small>
                )}
              </div>

              <div className="md:col-span-2">
                <Controller
                  name="earnsIncome"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <Checkbox
                        id="earnsIncome"
                        checked={field.value}
                        onChange={field.onChange}
                        className="mr-2"
                      />
                      <label htmlFor="earnsIncome" className="text-sm font-medium text-gray-700">
                        Member earns income
                      </label>
                    </div>
                  )}
                />
              </div>

            </div>
          </div>

          <Divider />

          {/* Address Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="address.street1" className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <Controller
                  name="address.street1"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.street1"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.street1 ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.street1 && (
                  <small className="text-red-500 mt-1 block">{errors.address.street1.message}</small>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="address.street2" className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address 2
                </label>
                <Controller
                  name="address.street2"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.street2"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className="w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label htmlFor="address.city" className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <Controller
                  name="address.city"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.city"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.city ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.city && (
                  <small className="text-red-500 mt-1 block">{errors.address.city.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="address.zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code
                </label>
                <Controller
                  name="address.zipCode"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.zipCode"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.zipCode ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.zipCode && (
                  <small className="text-red-500 mt-1 block">{errors.address.zipCode.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="address.country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <Controller
                  name="address.country"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.country"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.country ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.country && (
                  <small className="text-red-500 mt-1 block">{errors.address.country.message}</small>
                )}
              </div>
            </div>
          </div>


          {/* Notes */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Notes</h3>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <InputTextarea
                  id="notes"
                  value={field.value}
                  onChange={field.onChange}
                  className="w-full"
                  rows={4}
                  placeholder="Any additional notes about the member..."
                />
              )}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={handleDialogHide}
              disabled={isSubmitting}
            />
            <Button
              type="submit"
              label="Review & Submit"
              icon="pi pi-check"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        visible={showConfirmation}
        onHide={() => !isSubmitting && setShowConfirmation(false)}
        message={renderConfirmationContent()}
        header="Confirm New Member"
        icon="pi pi-question-circle"
        accept={handleConfirmedSubmit}
        acceptLabel="Create Member"
        acceptClassName="p-button-success"
        reject={() => setShowConfirmation(false)}
        rejectLabel="Go Back"
        rejectClassName="p-button-outlined"
      />
    </>
  )
}

export default AddMemberModal